// Singleton state — module-scoped, never reset.
// Keeps Eruda's init/show/hide idempotent across remounts of DevToolsButton.
let erudaInstance: typeof import("eruda").default | null = null
let isShown = false
let isLoading = false

/**
 * Lazy-load and toggle Eruda mobile console.
 *
 * - First call: dynamic-imports `eruda`, calls `init()`, shows the panel.
 * - Subsequent calls: flip `isShown` and call `show()` / `hide()` on the
 *   singleton instance. We track visibility ourselves because Eruda toggles
 *   an inner shadow-DOM element, not the `#eruda` container, so DOM-based
 *   detection from outside the shadow root is unreliable.
 * - Safe to call from a click handler without awaiting (fire-and-forget).
 *
 * Production safety: env-gated. Vite tree-shakes the dynamic import out of
 * production builds because the branch becomes statically unreachable.
 */
export const toggleEruda = async (): Promise<void> => {
  if (import.meta.env.VITE_NODE_ENV === "production") return
  if (isLoading) return

  if (erudaInstance) {
    if (isShown) {
      erudaInstance.hide()
    } else {
      erudaInstance.show()
    }
    isShown = !isShown
    return
  }

  isLoading = true
  try {
    const { default: eruda } = await import("eruda")
    eruda.init()
    eruda.show()
    erudaInstance = eruda
    isShown = true
  } finally {
    isLoading = false
  }
}

// Singleton state — module-scoped, never reset.
// Cleanup happens via stopGpuStress() which removes the container from the DOM.
let stressContainer: HTMLDivElement | null = null

/**
 * Append `count` fixed-position divs with backdrop-filter to document.body to
 * stress the GPU compositor. Each div forces its own composite layer via
 * `transform: translateZ(N)` and applies a 2px backdrop blur over the whole
 * viewport — the combination is a worst-case load for mobile GPUs and helps
 * reproduce performance bugs (e.g. WebView 147 jank).
 *
 * Idempotent: subsequent calls without stop are no-ops.
 */
export const startGpuStress = (count: number): void => {
  if (stressContainer) return

  const container = document.createElement("div")
  container.id = "__gpu-stress-container"

  for (let i = 0; i < count; i++) {
    const div = document.createElement("div")
    div.style.cssText = `
      position: fixed;
      inset: 0;
      background: rgba(255, 255, 255, 0.01);
      backdrop-filter: blur(2px);
      -webkit-backdrop-filter: blur(2px);
      transform: translateZ(${i}px);
      pointer-events: none;
      z-index: -1;
    `
    container.appendChild(div)
  }

  document.body.appendChild(container)
  stressContainer = container
}

/**
 * Remove the GPU stress container and reset state.
 * Idempotent: no-op if stress is not active.
 */
export const stopGpuStress = (): void => {
  if (!stressContainer) return
  stressContainer.remove()
  stressContainer = null
}
